import React from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable, { TableColumn, TableStyles } from 'react-data-table-component';
import { Badge, Card, Dropdown } from 'react-bootstrap';
import {
    useGetLoanRequests,
    useGetLoanRequestSummary,
    useGetLatestLeadNotes
} from '../../../services/loanService';
import type { LoanRequest } from '../../../services/schemas/loanServiceSchemas';
import { formatCurrency } from '../../../utils/formatters';
import LeadNotesModal from './LeadNotesModal';
import '../customers/LoanCustomers.css';

/**
 * Every loan request the business has been asked to fund, and where each one has got to: what was
 * asked for, what the application carries, what has been paid out and the stage it is at. This is
 * the view that stands in for the loan request admin spreadsheet.
 *
 * Requests that began as a lead carry the lead's date, referral and comments; those captured
 * straight into the system leave those columns empty. Unsubmitted drafts are not requests, so they
 * are left out.
 */

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const tableStyles: TableStyles = {
    headRow: {
        style: {
            backgroundColor: '#f8f9fa',
            borderBottom: '2px solid #dee2e6',
            minHeight: '48px',
        },
    },
    headCells: {
        style: {
            fontWeight: 700,
            fontSize: '0.78rem',
            letterSpacing: '0.03em',
            textTransform: 'uppercase',
            color: '#212529',
        },
    },
    rows: {
        style: {
            minHeight: '62px',
        },
    },
};

// The application stage, coloured so a stalled request is visible at a glance.
const STATUS_VARIANT: Record<string, string> = {
    SUBMITTED: 'primary',
    DOCUMENT_COLLECTION: 'info',
    VERIFICATION_IN_PROGRESS: 'info',
    VERIFICATION_COMPLETE: 'info',
    EVALUATION_IN_PROGRESS: 'info',
    COLLATERAL_VERIFICATION: 'info',
    AGREEMENT_PREPARATION: 'warning',
    AGREEMENT_READY: 'warning',
    PENDING_DISBURSEMENT: 'warning',
    DISBURSEMENT_IN_PROGRESS: 'warning',
    ACTIVE: 'success',
    COMPLETED: 'success',
    REJECTED: 'danger',
    REJECTED_EVALUATION: 'danger',
    REJECTED_COLLATERAL: 'danger',
    VERIFICATION_REJECTED: 'danger',
    CANCELLED: 'danger',
};

const formatDateTime = (iso?: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-ZA', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
    });
};

const formatDate = (iso?: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-ZA');
};

const amount = (value?: number) => (
    value === undefined || value === null
        ? <span className="text-muted fst-italic">—</span>
        : <span className="fw-semibold">{formatCurrency(value)}</span>
);

const LoanRequests: React.FC = () => {
    const navigate = useNavigate();
    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(25);
    const [notesTarget, setNotesTarget] = React.useState<LoanRequest | null>(null);

    const { data, isLoading, isError } = useGetLoanRequests({ page, size: pageSize });
    // Across the whole book, not just this page, so the figures do not change as you page through.
    const { data: summary } = useGetLoanRequestSummary();
    const rows = data?.content ?? [];

    // The last note per lead, in one call, so the comments column costs a request per page.
    const leadIds = rows.map(row => row.leadId).filter((id): id is string => !!id);

    const { data: latestNotes } = useGetLatestLeadNotes(
        { leadIds },
        { query: { enabled: leadIds.length > 0 } }
    );
    const lastNoteByLeadId = new Map((latestNotes ?? []).map(note => [note.leadId, note]));

    const columns: TableColumn<LoanRequest>[] = [
        {
            name: 'Date Initiated',
            selector: row => row.dateInitiated || '',
            cell: row => <span className="text-nowrap">{formatDate(row.dateInitiated)}</span>,
            sortable: true,
            width: '120px',
        },
        {
            name: 'Name',
            selector: row => row.name || '',
            cell: row => (
                <div className="py-2">
                    <div className="fw-semibold">{row.name || '—'}</div>
                    {row.applicationNumber && (
                        <small className="text-muted font-monospace">{row.applicationNumber}</small>
                    )}
                </div>
            ),
            sortable: true,
            minWidth: '200px',
        },
        {
            name: 'Client',
            selector: row => (row.existingClient ? 'Existing' : 'New'),
            cell: row => (
                <Badge bg={row.existingClient ? 'secondary' : 'primary'}>
                    {row.existingClient ? 'Existing' : 'New'}
                </Badge>
            ),
            width: '110px',
        },
        {
            name: 'Contact',
            selector: row => row.contactNumber || '',
            cell: row => (
                <div className="py-2">
                    <div>{row.contactNumber || '—'}</div>
                    {row.email && <small className="text-muted text-break">{row.email}</small>}
                </div>
            ),
            minWidth: '190px',
        },
        {
            name: 'Client ID',
            selector: row => row.clientId || '',
            cell: row => <span className="font-monospace small">{row.clientId || '—'}</span>,
            width: '140px',
        },
        {
            name: 'Requested',
            selector: row => row.requestedAmount ?? 0,
            cell: row => amount(row.requestedAmount),
            sortable: true,
            right: true,
            width: '130px',
        },
        {
            name: 'Approved',
            selector: row => row.approvedAmount ?? 0,
            cell: row => amount(row.approvedAmount),
            sortable: true,
            right: true,
            width: '130px',
        },
        {
            name: 'Disbursed',
            selector: row => row.disbursedAmount ?? 0,
            cell: row => amount(row.disbursedAmount),
            sortable: true,
            right: true,
            width: '130px',
        },
        {
            name: 'Request Status',
            selector: row => row.requestStatus || '',
            cell: row => {
                if (!row.requestStatus) return <span className="text-muted fst-italic">—</span>;
                const variant = STATUS_VARIANT[row.requestStatus] ?? 'secondary';
                return (
                    <Badge bg={variant} className="text-wrap">
                        {row.requestStatus.replace(/_/g, ' ')}
                    </Badge>
                );
            },
            sortable: true,
            minWidth: '170px',
        },
        {
            name: 'Action Date',
            selector: row => row.actionDate || '',
            cell: row => <span className="text-nowrap small">{formatDateTime(row.actionDate)}</span>,
            sortable: true,
            width: '150px',
        },
        {
            name: 'Referral',
            selector: row => row.referral || '',
            cell: row => (
                <span className="small">{row.referral ? row.referral.replace(/_/g, ' ') : '—'}</span>
            ),
            width: '120px',
        },
        {
            name: 'Comments',
            cell: row => {
                const note = row.leadId ? lastNoteByLeadId.get(row.leadId) : undefined;
                if (!note) return <span className="text-muted fst-italic">No comments</span>;
                return (
                    <div className="py-2" title={note.note}>
                        <div className="text-truncate" style={{ maxWidth: '220px' }}>{note.note}</div>
                        <small className="text-muted">{note.createdBy || 'Unknown'}</small>
                    </div>
                );
            },
            minWidth: '240px',
            wrap: true,
        },
        {
            name: 'Actions',
            cell: row => (
                <Dropdown align="end">
                    <Dropdown.Toggle
                        variant="light"
                        size="sm"
                        id={`request-actions-${row.applicationId}`}
                        className="rounded-pill d-flex align-items-center no-caret"
                    >
                        <span>Actions</span>
                        <i className="fa fa-caret-down ms-2"></i>
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item
                            className="action-view"
                            disabled={!row.applicationId}
                            onClick={() => row.applicationId && navigate(`/application/${row.applicationId}`)}
                        >
                            <i className="fa fa-eye me-2"></i>
                            View / Trace Application
                        </Dropdown.Item>
                        <Dropdown.Item
                            className="action-view"
                            disabled={!row.leadId}
                            title={row.leadId ? undefined : 'Comments are kept against a lead; this request was captured directly'}
                            onClick={() => row.leadId && setNotesTarget(row)}
                        >
                            <i className="fa fa-sticky-note me-2"></i>
                            Comments
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            ),
            minWidth: '150px',
            ignoreRowClick: true,
        },
    ];

    return (
        <div className="container-fluid px-4 py-3">
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <div>
                    <h4 className="mb-1 fw-bold">Loan Requests</h4>
                    <p className="text-muted mb-0 small">
                        Every submitted request and where it now stands.
                    </p>
                </div>
                {data?.totalElements !== undefined && (
                    <Badge bg="light" text="dark" className="border px-3 py-2">
                        {data.totalElements} requests
                    </Badge>
                )}
            </div>

            {/* Where the book stands at a glance. */}
            <div className="row g-4 mb-4">
                {[
                    {
                        label: 'Total Requests',
                        value: summary?.totalRequests ?? '—',
                        icon: 'fa-file-text-o', color: 'primary', border: 'rgba(0,123,255,0.2)',
                    },
                    {
                        label: 'In Progress',
                        value: summary?.inProgress ?? '—',
                        icon: 'fa-hourglass-half', color: 'info', border: 'rgba(23,162,184,0.2)',
                    },
                    {
                        label: 'Approved Value',
                        value: summary?.totalApproved !== undefined
                            ? formatCurrency(summary.totalApproved) : '—',
                        icon: 'fa-check-circle', color: 'success', border: 'rgba(40,167,69,0.2)',
                    },
                    {
                        label: 'Disbursed',
                        value: summary?.totalDisbursed !== undefined
                            ? formatCurrency(summary.totalDisbursed) : '—',
                        icon: 'fa-money', color: 'dark', border: 'rgba(33,37,41,0.2)',
                    },
                ].map(card => (
                    <div key={card.label} className="col-lg-3 col-md-6">
                        <Card className="border-0 shadow-sm h-100">
                            <Card.Body className="p-4">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                        <p className="text-muted small mb-1">{card.label}</p>
                                        <h3 className={`mb-0 fw-bold text-${card.color}`}>{card.value}</h3>
                                    </div>
                                    <div className="rounded-circle p-3" style={{ backgroundColor: '#f8f9fa', border: `1px solid ${card.border}` }}>
                                        <i className={`fa ${card.icon} fa-2x text-${card.color}`} style={{ display: 'block' }}></i>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                ))}
            </div>

            {(summary?.completed !== undefined || summary?.declined !== undefined) && (
                <p className="text-muted small mb-3">
                    <i className="fa fa-check me-1 text-success"></i>
                    {summary?.completed ?? 0} completed
                    <span className="mx-2">·</span>
                    <i className="fa fa-times me-1 text-danger"></i>
                    {summary?.declined ?? 0} rejected or cancelled
                </p>
            )}

            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    {isError ? (
                        <div className="text-center py-5">
                            <i className="fa fa-exclamation-triangle fa-2x text-warning mb-2 d-block"></i>
                            <p className="text-muted mb-0">Unable to load converted leads.</p>
                        </div>
                    ) : (
                        <DataTable
                            columns={columns}
                            data={rows}
                            progressPending={isLoading}
                            pagination
                            paginationServer
                            paginationTotalRows={data?.totalElements ?? 0}
                            paginationPerPage={pageSize}
                            paginationRowsPerPageOptions={PAGE_SIZE_OPTIONS}
                            onChangePage={newPage => setPage(newPage - 1)}
                            onChangeRowsPerPage={newSize => { setPageSize(newSize); setPage(0); }}
                            onRowClicked={row => row.applicationId && navigate(`/application/${row.applicationId}`)}
                            pointerOnHover
                            highlightOnHover
                            striped
                            responsive
                            customStyles={tableStyles}
                            noDataComponent={
                                <div className="text-center py-5">
                                    <i className="fa fa-exchange fa-2x text-muted mb-2 d-block"></i>
                                    <p className="text-muted mb-0">No loan requests yet.</p>
                                </div>
                            }
                        />
                    )}
                </Card.Body>
            </Card>

            <LeadNotesModal
                leadId={notesTarget?.leadId ?? null}
                leadName={notesTarget?.name}
                onHide={() => setNotesTarget(null)}
            />
        </div>
    );
};

export default LoanRequests;
